import { useMutation } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../../api/client';
import PhoneVerificationModal from '../../../components/phone-verification-modal';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../../components/ui/tooltips';
import { useApp } from '../../../context/usePortal';
import { cn } from '../../../lib/utils';

const TIMEOUT = 30;

const DEACTIVATION_INFO = [
  {
    text: (
      <>
        Your account will be{' '}
        <span className="text-black">temporarily deactivated</span> for 30 days
      </>
    )
  },
  {
    text: (
      <>
        You{' '}
        <span className="text-black">will not be able to make payments</span>{' '}
        with AyeFace during this time
      </>
    )
  },
  {
    text: (
      <>All rewards and saved payment methods will be temporarily disabled</>
    )
  },
  {
    text: (
      <>
        You can <span className="text-black">reactivate anytime</span> by
        logging into your user portal
      </>
    )
  },
  {
    text: (
      <>
        After 30 days with no login, your account will be{' '}
        <span className="text-black">permanently deleted</span>
      </>
    )
  }
];

const DeleteAccount: React.FC = () => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const { user } = useApp();

  const phoneNumber = `${user?.country_code}-${user?.user_phone_number}`;

  const [deleteStep, setDeleteStep] = useState<'select' | 'confirm' | 'done'>(
    'select'
  );

  // State on confirm step
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const handleOpenDialog = useCallback(
    () => setShowDeleteConfirmation(true),
    []
  );

  // Placeholder for actual deletion logic
  const handleDeleteAccount = useCallback(() => {
    // TODO: Implement account deletion logic
    // setShowDeleteConfirmation(false);
    setShowPhoneVerification(true);
    setDeleteStep('confirm');
  }, []);

  // Placeholder for actual deactivation logic
  const handleDeactivateAccount = useCallback(() => {
    // TODO: Implement account deactivation logic
    // setShowDeleteConfirmation(false);
    setShowPhoneVerification(true);
  }, []);

  const verifyOtpForCurrentPhone = useMutation({
    mutationFn: async (data: { otp: string }) => {
      await apiClient.post('/users/verify/otp', data);
      return data;
    },
    onSuccess: () => {
      setShowPhoneVerification(false);
      setShowDeleteConfirmation(true);
    },
    onError: (error) => {
      console.error('Error validating phone number:', error);
      if (
        (error as any)?.response?.data?.error?.code === 'OTP_FAILED_ATTEMPTS'
      ) {
        return;
      }
      toast.error(
        (error as any)?.response?.data?.error?.message || 'Invalid phone number'
      );
    }
  });

  const handleSubmitOTP = (otp: string) => {
    verifyOtpForCurrentPhone.mutate({
      otp
    });
  };

  const titleMapping = {
    select:
      'Are you sure you want to delete your account? Or, choose to deactivate your account instead',
    confirm: 'Delete Your Account',
    done: 'Account Deleted'
  };

  const handleCloseDialog = useCallback(() => {
    setShowDeleteConfirmation(false);
    setDeleteStep('select');
    setIsTermsAccepted(false);
  }, []);

  const handleConfirmDeletion = useCallback(() => {
    setDeleteStep('done');
  }, []);

  const [timer, setTimer] = useState(TIMEOUT);

  useEffect(() => {
    let interval: any;
    if (deleteStep === 'done') {
      setTimer(TIMEOUT); // Reset timer on open
      interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [deleteStep]);

  return (
    <>
      <section className="px-4 pb-3 pt-2 sm:px-6">
        <div className="rounded-lg bg-[#FE724C26] p-4">
          <h2 className="text-md mb-1 flex flex-row items-center gap-x-2 font-medium text-primary">
            Delete Your Account
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Info className="mb-1 h-4 w-4 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="ml-36 w-56 bg-[#F3F3F3]">
                  <p className="text-xs font-extralight text-[#101B2C]">
                    Delete account is not available at the moment. If you need
                    assistance, please contact us
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </h2>
          <p className="mb-3 text-sm text-gray-500">
            By deleting your account, you'll lose all rewards and won't be able
            to make payments or log in to the User Portal.
          </p>
          <Button type="button" onClick={handleOpenDialog} disabled>
            Delete Account
          </Button>
        </div>
      </section>

      <Dialog open={showDeleteConfirmation} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-[24rem] rounded-3xl">
          <DialogHeader className="mt-5">
            <DialogTitle
              className={cn(
                deleteStep === 'done' ? 'text-center' : 'text-left'
              )}
            >
              {titleMapping[deleteStep]}
            </DialogTitle>
          </DialogHeader>

          {deleteStep === 'select' && (
            <>
              <p className="text-sm">What happens when you deactivate:</p>
              <ul className="ml-5 mt-1 list-disc">
                {DEACTIVATION_INFO.map((item, idx) => (
                  <li key={idx} className="text-sm text-[#5B5959]">
                    {item.text}
                  </li>
                ))}
              </ul>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button onClick={handleDeleteAccount}>Delete Account</Button>
                <Button
                  variant="outline"
                  onClick={handleDeactivateAccount}
                  className="border-primary text-primary hover:text-primary"
                >
                  Deactivate instead
                </Button>
              </div>
            </>
          )}

          {deleteStep === 'confirm' && (
            <>
              <div>
                <p className="mb-2 text-sm">What will be deleted:</p>
                <p className="text-sm">
                  All your collected rewards, saved payment methods, Face ID,
                  phone number, and any other personal data will be permanently
                  deleted.
                </p>
              </div>

              <div className="items-top mt-4 flex w-full space-x-2">
                <Checkbox
                  id="terms1"
                  checked={isTermsAccepted}
                  onCheckedChange={(checked) => setIsTermsAccepted(!!checked)}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="terms1"
                    className="text-sm font-light leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I understand that I won't be able to recover my account
                  </label>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2">
                <Button
                  onClick={handleConfirmDeletion}
                  disabled={!isTermsAccepted}
                >
                  Confirm Deletion
                </Button>
                <Button
                  variant="outline"
                  // onClick={handleDeactivateAccount}
                  className="border-primary text-primary hover:text-primary"
                >
                  Cancel
                </Button>
              </div>
            </>
          )}

          {deleteStep === 'done' && (
            <div className="text-center">
              Redirecting you to the login page in <strong>{timer}s</strong>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PhoneVerificationModal
        phoneNumber={phoneNumber}
        isOpen={showPhoneVerification}
        onClose={() => setShowPhoneVerification(false)}
        onVerify={(otp) => handleSubmitOTP(otp)}
        verifyError={verifyOtpForCurrentPhone.error}
      />
    </>
  );
};

export default DeleteAccount;
